import type { Clip, Project, SubStyle } from '../types'

// Text geometry shared by the preview renderer and the export mapper so the
// MP4 matches what the user sees. Mirrors the server's drawtext layout
// (server/src/export.rs): centred lines, line height = 1.25 × font size,
// block centred on `cy`; fonts are the same Inter TTFs the server uses.

export const FONT_FAMILY = 'InterEditor'

export interface TextBlock {
  lines: string[]
  fontSize: number
  font: string
  cx: number
  cy: number
  lineHeight: number
  color: string
  background: string | null
  outline: { width: number; color: string } | null
  shadow: boolean
  opacity: number
}

let measureCtx: CanvasRenderingContext2D | null = null
function mctx(): CanvasRenderingContext2D {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')!
  return measureCtx
}

export function fontString(weight: number, italic: boolean, size: number): string {
  return `${italic ? 'italic ' : ''}${weight} ${size.toFixed(1)}px ${FONT_FAMILY}`
}

/** Greedy word wrap honouring explicit newlines. */
export function wrapText(text: string, font: string, maxWidth: number): string[] {
  const ctx = mctx()
  ctx.font = font
  const out: string[] = []
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean)
    if (!words.length) continue
    let line = words[0]
    for (let i = 1; i < words.length; i++) {
      const test = `${line} ${words[i]}`
      if (ctx.measureText(test).width > maxWidth) {
        out.push(line)
        line = words[i]
      } else line = test
    }
    out.push(line)
  }
  return out
}

export function effectiveSubStyle(p: Project, c: Clip): SubStyle {
  return c.subStyle ?? p.subtitleStyle
}

function transparent(c: string | undefined): boolean {
  if (!c) return true
  const v = c.trim().toLowerCase()
  if (v === 'transparent' || v === '') return true
  if (/^#[0-9a-f]{8}$/.test(v) && v.endsWith('00')) return true
  return false
}

export function textBlock(p: Project, c: Clip): TextBlock | null {
  const W = p.settings.width
  const H = p.settings.height
  const t = c.transform
  const scale = Math.max(0.05, t.scale)
  if (c.kind === 'text' && c.text) {
    const tx = c.text
    const weight = tx.bold ? 700 : 400
    const fs = (tx.fontSize > 0 ? tx.fontSize : 64) * scale
    const font = fontString(weight, tx.italic, fs)
    const lines = wrapText(tx.text, font, W * 0.92)
    if (!lines.length) return null
    return {
      lines, fontSize: fs, font, cx: W / 2 + t.x, cy: H / 2 + t.y, lineHeight: fs * 1.25,
      color: tx.color || '#ffffff',
      background: transparent(tx.backgroundColor) ? null : tx.backgroundColor,
      outline: null, shadow: false, opacity: t.opacity,
    }
  }
  if (c.kind === 'subtitle') {
    const st = effectiveSubStyle(p, c)
    const weight = st.fontWeight >= 800 ? 800 : st.fontWeight >= 600 ? 700 : 400
    const fs = (st.fontSize > 0 ? st.fontSize : 56) * scale
    const font = fontString(weight, false, fs)
    const lines = wrapText(c.rawText ?? '', font, W * 0.86)
    if (!lines.length) return null
    const f = st.position === 'top' ? 0.1 : st.position === 'middle' ? 0.5 : 0.88
    const cy = c.positionMode === 'free' ? H / 2 + t.y : H * f
    return {
      lines, fontSize: fs, font, cx: W / 2 + t.x, cy, lineHeight: fs * 1.25,
      color: st.color || '#ffffff',
      background: transparent(st.backgroundColor) ? null : st.backgroundColor,
      outline: st.outline ? { width: Math.max(2, fs * 0.06), color: st.outlineColor || '#000000' } : null,
      shadow: st.shadow, opacity: t.opacity,
    }
  }
  return null
}

export interface Box { x: number; y: number; w: number; h: number; rotation: number }

export function drawTextBlock(ctx: CanvasRenderingContext2D, b: TextBlock): Box {
  ctx.save()
  ctx.globalAlpha = Math.max(0, Math.min(1, b.opacity))
  ctx.font = b.font
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.lineJoin = 'round'
  const blockH = b.lineHeight * b.lines.length
  const top = b.cy - blockH / 2
  let maxW = 0
  b.lines.forEach((line, i) => {
    const y = top + b.lineHeight * i + b.lineHeight / 2
    const m = ctx.measureText(line)
    maxW = Math.max(maxW, m.width)
    if (b.background) {
      const pad = Math.max(4, b.fontSize * 0.25)
      const asc = m.actualBoundingBoxAscent || b.fontSize * 0.72
      const desc = m.actualBoundingBoxDescent || b.fontSize * 0.2
      ctx.fillStyle = b.background
      ctx.fillRect(b.cx - m.width / 2 - pad, y - (asc + desc) / 2 - pad, m.width + pad * 2, asc + desc + pad * 2)
    }
    if (b.shadow) {
      const o = Math.max(2, b.fontSize * 0.05)
      ctx.fillStyle = 'rgba(0,0,0,0.65)'
      ctx.fillText(line, b.cx + o, y + o)
    }
    if (b.outline) {
      ctx.strokeStyle = b.outline.color
      ctx.lineWidth = b.outline.width * 2
      ctx.strokeText(line, b.cx, y)
    }
    ctx.fillStyle = b.color
    ctx.fillText(line, b.cx, y)
  })
  ctx.restore()
  const pad = b.background ? Math.max(4, b.fontSize * 0.25) : 0
  return { x: b.cx - maxW / 2 - pad, y: top, w: maxW + pad * 2, h: blockH, rotation: 0 }
}
